import { createContext, type ReactNode, use, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

// A route-keyed snapshot of what the user is actually looking at (their score,
// persona, plan day), so the bar's [App context] block carries live values, not
// just a route name. Keyed by route so a snapshot set on /today can never leak
// stale numbers into a question asked from /plan.
export interface PrybarScreen {
  route: string;
  data: Record<string, unknown>;
}

interface ScreenStore {
  screen: PrybarScreen | null;
  set: (screen: PrybarScreen) => void;
}

const PrybarScreenContext = createContext<ScreenStore>({ screen: null, set: () => {} });

export function PrybarScreenProvider({ children }: { children: ReactNode }) {
  const [screen, setScreen] = useState<PrybarScreen | null>(null);
  const value: ScreenStore = { screen, set: setScreen };
  return <PrybarScreenContext.Provider value={value}>{children}</PrybarScreenContext.Provider>;
}

// Read the snapshot only when it belongs to the current route; otherwise the
// caller gets an empty object and falls back to the route description alone.
// Shared empty so a route with no published context returns a stable reference
// (callers memoise on it; a fresh {} each render would defeat that).
const EMPTY_SCREEN: Record<string, unknown> = Object.freeze({});

export function usePrybarScreenData(route: string): Record<string, unknown> {
  const { screen } = use(PrybarScreenContext);
  return screen && screen.route === route ? screen.data : EMPTY_SCREEN;
}

// Pages call this with whatever is meaningfully on screen. The snapshot is
// stamped with the current route so it self-expires on navigation.
export function useSetPrybarScreen(data: Record<string, unknown>): void {
  const { pathname } = useLocation();
  const { set } = use(PrybarScreenContext);
  const key = JSON.stringify(data);
  // biome-ignore lint/correctness/useExhaustiveDependencies: key is the serialised form of data
  useEffect(() => {
    set({ route: pathname, data });
  }, [pathname, key, set]);
}
